import Decimal from 'app/src/data/base/Decimal';
import SalePayment from 'app/src/data/models/SalePayment';
import { getCurrency } from 'helpers/locale';
import InvoicingCustomer from 'root/apps/QuoteMachineIntegration/state/Customer';
import Employee from 'root/apps/QuoteMachineIntegration/state/Employee';
import {
  DepositPayment,
  InvoicePayment,
  InvoicingSalePayment,
} from 'root/apps/QuoteMachineIntegration/state/InvoicingPayload';
import { ItemFee } from 'root/apps/QuoteMachineIntegration/state/ItemFee';
import LineItem from 'root/apps/QuoteMachineIntegration/state/LineItem';
import Location from 'root/apps/QuoteMachineIntegration/state/Location';
import {
  generateTaxClassTotals,
  TaxClassTotal,
} from 'root/apps/QuoteMachineIntegration/state/TaxClassTotal';

import { WorkOrder } from './WorkOrder';

enum LineItemType {
  ITEM_FEE = 'item_fee',
  ITEM_FEE_REFUND = 'item_fee_refund',
}

export default class Invoice {
  lsSaleId: string;
  lsAccountId: string;
  lsRegisterId: string;
  currency: string;
  totalFeesAmount: number;
  subtotal: number;
  total: number;
  totalTipAmount: number;
  discountTotal: number;
  taxTotal: number;
  lineItems: LineItem[];
  itemFees: ItemFee[];
  workOrders: WorkOrder[];
  location: Location;
  employee: Employee;
  customer: InvoicingCustomer;
  lsCreditAccountId: string;
  saleTaxCalculations: TaxClassTotal[];
  payments: InvoicingSalePayment[];
  invoicePayments: InvoicePayment[];
  depositPayments: DepositPayment[];

  constructor(
    sale: Sale,
    employee: Employee,
    location: Location,
    accountId: string,
    payments: InvoicingSalePayment[],
    invoicePayments: InvoicePayment[],
    depositPayments: DepositPayment[],
    giftCardSalePayments: SalePayment[],
    depositToAdd: Decimal
  ) {
    const totalInvoicePaymentAmount = invoicePayments?.reduce(
      (total, payment) => total.plus(Number(payment.amount)),
      new Decimal(0)
    );

    const invoicingAmount = new Decimal(totalInvoicePaymentAmount).plus(depositToAdd);
    this.lsSaleId = `${sale.saleID}`;
    this.lsAccountId = accountId || '';
    this.lsRegisterId = `${sale.registerID}` || '';
    this.currency = getCurrency().currencyCode;
    this.totalFeesAmount = Number(sale.calcItemFees) || 0;
    this.subtotal = new Decimal(sale.calcSubtotal ?? 0).plus(invoicingAmount).toNumber();
    this.total = new Decimal(sale.calcTotal ?? 0).plus(invoicingAmount).toNumber();
    this.totalTipAmount = new Decimal(sale.calcTips ?? 0).toNumber();
    this.discountTotal = Number(sale.calcDiscount) || 0;
    this.taxTotal = Number(sale.taxTotal) || 0;
    this.employee = employee;
    this.location = location;
    this.customer = new InvoicingCustomer(sale.Customer);
    this.lsCreditAccountId = `${sale.Customer?.creditAccountID}` || '';
    this.lineItems = [];
    this.itemFees = [];
    this.workOrders = [];
    this.payments = payments;
    this.saleTaxCalculations = [];
    this.invoicePayments = invoicePayments;
    this.depositPayments = depositPayments;

    if (sale?.TaxClassTotals?.Tax) {
      const taxes: Tax[] = Array.isArray(sale.TaxClassTotals.Tax)
        ? sale.TaxClassTotals.Tax
        : [sale.TaxClassTotals.Tax];

      this.saleTaxCalculations = generateTaxClassTotals(taxes);
    }

    if (sale?.SaleLines?.SaleLine) {
      const saleLines: SaleLine[] = Array.isArray(sale.SaleLines.SaleLine)
        ? sale.SaleLines.SaleLine
        : [sale.SaleLines.SaleLine];

      this.itemFees = saleLines
        .filter((line) => line.lineType === LineItemType.ITEM_FEE)
        .map((line) => new ItemFee(line));

      // Get parent work orders from line items
      this.workOrders = saleLines
        .filter((line) => line.SaleLineWorkOrder?.workOrderType === 'parent')
        .map((line) => new WorkOrder(line));

      this.lineItems = saleLines
        .filter(
          (line) =>
            line.lineType !== LineItemType.ITEM_FEE &&
            (line.SaleLineWorkOrder?.workOrderType ?? '') !== 'parent' // remove parent work order saleLines
        )
        .map((line) => {
          const lineItem = new LineItem(line);
          const associatedGiftCardPayment = (giftCardSalePayments ?? []).find(
            (payment) => payment.getSaleLineID().toString() === lineItem.lsSaleLineId
          );

          if (associatedGiftCardPayment) {
            const giftCardAmount = Math.abs(Number(associatedGiftCardPayment.get('amount')));
            lineItem.total = giftCardAmount;
            lineItem.subtotal = giftCardAmount;
            lineItem.unitPrice = giftCardAmount;
            this.total = new Decimal(this.total).plus(giftCardAmount).toNumber();
            this.subtotal = new Decimal(this.subtotal).plus(giftCardAmount).toNumber();
          }

          return lineItem;
        });
    }
  }
}
